import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  useWorkloadReliabilityMetricsQuery,
  WorkloadReliabilityMetricsQueryData,
} from '@cast/split-me/hooks';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { isRangeSupported } from '../../_utils/isRangeSupported';

type UseInventoryWorkloadReliabilityData = {
  data?: WorkloadReliabilityMetricsQueryData;
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
  isRangeSupported: boolean;
};

export const useInventoryWorkloadReliabilityData =
  (): UseInventoryWorkloadReliabilityData => {
    const { workload } = useContext(WorkloadDetailsContext);
    const { cluster } = useCluster();
    const { from, to, timeZone } = useInventoryWorkloadsTimeRangeSelection();

    const toParam = to ? dayjs.utc(to) : dayjs.utc(0);
    const hasRange = !!from && !!to;
    const rangeSupported = hasRange && isRangeSupported(from, to);

    const tableDateRange = useMemo(() => {
      if (!rangeSupported) {
        return undefined;
      }

      const tableTo = dayjs(toParam).tz(timeZone).startOf('day').add(1, 'day');

      return {
        from: tableTo.subtract(30, 'day').toISOString(),
        to: tableTo.toISOString(),
      };
    }, [toParam, timeZone]);

    const {
      data: dailyTableData,
      isLoading,
      error,
      refetch,
    } = useWorkloadReliabilityMetricsQuery(
      {
        clusterId: cluster.id,
        namespace: workload?.namespace || '',
        workloadType: workload?.kind || '',
        workloadName: workload?.name || '',
        startTime: tableDateRange?.from || '',
        endTime: tableDateRange?.to || '',
        stepSeconds: 86400,
        timezone: timeZone,
      },
      {
        enabled:
          rangeSupported &&
          !!tableDateRange &&
          !!workload?.namespace &&
          !!workload?.kind &&
          !!workload?.name,
      }
    );

    return {
      data: dailyTableData,
      isLoading,
      error,
      refetch,
      isRangeSupported: rangeSupported,
    };
  };
